import React from "react";
import BlankStateV2 from "@ui/BlankStateV2";
import { SimpleGraph } from "@ui/Illustration/react";
import { useUserCanEdit } from "./hook/useUserCanEdit";
import { Navigate, Outlet, useOutletContext, useParams } from "react-router";
import { useUserCanRead } from "./hook/useUserCanRead";
import { useActiveAnalysisCount } from "../../../api/useActiveAnalysesStream";
import t from "../../../i18n";
import { toPath, useMatchPattern } from "../../../router/helpers/path";
import { useTranslations } from "../../../api/useTranslations";
import type { Project } from "../../../api/useProjects";
import { ProjectGlobalSidePanelLayout } from "../GlobalSidePanelLayout";

export const useCanWorkOnDashboards = (projectId: string) => {
  const analysisCount = useActiveAnalysisCount(projectId, null);
  return analysisCount > 0;
};

export const Dashboards = () => {
  const { projectId, dashboardId } = useParams();
  if (!projectId) throw new Error("no project");
  const project = useOutletContext<Project>();

  const translations = useTranslations();
  const canUserRead = useUserCanRead(projectId);
  const canUserEdit = useUserCanEdit(projectId);
  const canWorkOnDashboards = useCanWorkOnDashboards(projectId);

  const matchDashboardEdit = useMatchPattern("/projects/:projectId/dashboards/:dashboardId/edit");

  if (!canUserRead) {
    return <Navigate to={toPath("/projects/:projectId", { projectId })} replace />;
  }

  if (!canUserEdit && Boolean(matchDashboardEdit) && dashboardId) {
    return (
      <Navigate
        to={toPath("/projects/:projectId/dashboards/:dashboardId", { projectId, dashboardId })}
      />
    );
  }

  if (!project) throw new Error("no project");

  if (!canWorkOnDashboards) {
    return (
      <BlankStateV2
        title={t("project.dashboards.no_analyses.title", translations)}
        subtitle={t("project.dashboards.no_analyses.subtitle", translations)}
        picto={<SimpleGraph size="l" />}
      />
    );
  }

  return (
    <ProjectGlobalSidePanelLayout hidden={Boolean(matchDashboardEdit)} project={project}>
      <Outlet />
    </ProjectGlobalSidePanelLayout>
  );
};
